// Helpers for pages a Google (OAuth) sign-in redirects back to.

export interface OAuthReturnError {
  error: string;
  code: string;
  description: string;
}

/**
 * Supabase sends a failed OAuth sign-in back with error params in both the
 * query string and the fragment. Returns null on a normal visit.
 */
export function readOAuthError(): OAuthReturnError | null {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const pick = (key: string) => query.get(key) ?? hash.get(key) ?? '';
  const error = pick('error');
  const description = pick('error_description');
  if (!error && !description) return null;
  return { error, code: pick('error_code'), description };
}

/** Drops the OAuth error params so a reload doesn't repeat the message. */
export function clearOAuthParamsFromUrl(): void {
  window.history.replaceState(null, '', window.location.pathname);
}

/**
 * Message for a failed Google sign-in. `noAccountMessage` covers the
 * handle_new_user refusal (no invite / no ticket for that address), which
 * GoTrue reports as a generic database error.
 */
export function describeOAuthError(err: OAuthReturnError, noAccountMessage: string): string {
  const text = `${err.code} ${err.description}`.toLowerCase();
  if (text.includes('banned')) {
    return 'This account has been deactivated. Contact your administrator.';
  }
  if (text.includes('database error saving new user') || text.includes('signup')) {
    return noAccountMessage;
  }
  if (err.error === 'access_denied') {
    return 'Google sign-in was cancelled.';
  }
  return err.description || 'Google sign-in failed. Please try again.';
}

// An invite page that sends someone to Google needs to know, when they come
// back, that this page load is the return trip and for which invite.
export type OAuthInviteKind = 'member' | 'company';
const INVITE_KEY = 'steddy.oauthInvite';

export function markOAuthInvite(kind: OAuthInviteKind, token: string): void {
  try {
    sessionStorage.setItem(INVITE_KEY, JSON.stringify({ kind, token }));
  } catch {
    // without it the return trip just shows the invite form again
  }
}

/** The token of a pending Google round trip for this kind of invite, if any. */
export function peekOAuthInvite(kind: OAuthInviteKind): string | null {
  try {
    const raw = sessionStorage.getItem(INVITE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { kind?: string; token?: string };
    return parsed.kind === kind && typeof parsed.token === 'string' ? parsed.token : null;
  } catch {
    return null;
  }
}

export function clearOAuthInvite(): void {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    // ignore
  }
}
